import {
  Button,
  Card,
  CustomImage,
  DatePicker,
  IconDocument,
  IconWarningAlt,
  OptionInterface,
  Select,
  SelectOption,
  Table,
  TableColumns,
  TablePagination,
  TextBox,
} from "@veneer/core";
import "../../assets/css/views/metrics/metricsRealTime.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { useTranslation } from "react-i18next";
import moment from "moment";
import dataListService from "../../services/generalApi/dataList.service";
import metricSearchSelect from "./MetricsComponent/metricSearchSelect";
import metricsRealTimeService from "../../services/surveyApi/metricsRealTime.service";
import IconUserNotFound from "../../assets/img/attendance/VectoruserNotFound.png";
import { IOptionsMetricsRealTime } from "../../models/input/surveyApi/IOptionsMetrics";
import {
  FilterableFields,
  FormState,
  ICoordinatorBySubRegion,
  IMetricsList,
  ISupervisorByCoordinator,
  IValuesMetricsRealTime,
  MetricAbsence,
  MetricAbsenceCheckIn,
  MetricOption,
  MetricsCheckIn,
  MetricSos,
  MetricsTrainingMaterial,
  MetricsVisualStatus,
  MetricSync,
  MetricType,
  TableRow,
  ValidationState,
} from "../../models/input/surveyApi/IValuesMetrics";
import { decryptText } from "../../utils/encryptionUtils";
import LoaderModal from "../../components/Loader/loaderModal";

export default function MetricsRealTime() {
  const [t] = useTranslation("global");
  const languageID: any = useSelector((state: RootState) => state.languageId);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const { handleSearch } = metricSearchSelect<IOptionsMetricsRealTime>();
  const [isButtonValid, setIsButtonValid] = useState<boolean>(true);
  const user: any = useSelector((state: RootState) => state.user);
  const userRoleIdLogged = Number(decryptText(user.user?.roleId));
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const tableDataRef = useRef<TableRow[]>([]);
  const [filters, setFilters] = useState<FilterableFields>({});
  const [emptyMessage, setEmptyMessage] = useState<string>("");
  const [loadingMessage, setLoadingMessage] = useState<string>("");

  const initialOptions: IOptionsMetricsRealTime = {
    Coordinator: [],
    Supervisor: [],
    Metrics: [],
    FilteredCoordinator: [],
    FilteredSupervisor: [],
    FilteredMetrics: [],
  };

  const initialValidation: ValidationState = {
    Coordinator: true,
    Supervisor: true,
    Metrics: true,
  };

  const initialForm: FormState = {
    SupervisorID: 0,
    Metrics: null,
  };

  const initialValues: IValuesMetricsRealTime = {
    Supervisor: [],
    Coordinator: [],
    Metrics: [],
    StartDate: null,
    EndDate: null,
    Columns: [],
  };

  const [optionSelect, setOptionSelect] = useState<IOptionsMetricsRealTime>({
    ...initialOptions,
  });

  const [handleValidate, setHandleValidate] = useState<ValidationState>({
    ...initialValidation,
  });
  const [form, setForm] = useState<FormState>({ ...initialForm });
  const [values, setValues] = useState<IValuesMetricsRealTime>({
    ...initialValues,
  });

  const metricMessageMap: Record<MetricType, string> = {
    GetMetricRealTimeAbsence: t("header.notFoundAbsence"),

    GetMetricRealTimeAbsenceScheduling: t("header.notFoundAbsenceScheduling"),

    GetMetricRealTimeCheckIn: t("header.notFoundCheckIn"),

    GetMetricRealTimeEsfCoverage: t("header.notFoundEsfCoverage"),

    GetMetricRealTimeSosPc: t("header.notFoundSosPrintAndPc"),

    GetMetricRealTimeSosPrint: t("header.notFoundSosPrintAndPc"),

    GetMetricRealTimeSync: t("header.notFoundSync"),

    GetMetricRealTimeTrainigMaterial: t("header.notFoundTrainingMaterial"),

    GetMetricRealTimeVisualPcStatus: t("header.notFoundVisualPrintAndPc"),

    GetMetricRealTimeVisualPrintStatus: t("header.notFoundVisualPrintAndPc"),
  };

  const loadingMessageMap: Record<MetricType, string> = {
    GetMetricRealTimeAbsence: t("header.getMetricRealTimeAbsence"),

    GetMetricRealTimeAbsenceScheduling: t(
      "header.getMetricRealTimeAbsenceScheduling"
    ),

    GetMetricRealTimeCheckIn: t("header.getMetricRealTimeCheckIn"),

    GetMetricRealTimeEsfCoverage: t("header.getMetricRealTimeEsfCoverage"),

    GetMetricRealTimeSosPc: t("header.getMetricRealTimeSos"),

    GetMetricRealTimeSosPrint: t("header.getMetricRealTimeSos"),

    GetMetricRealTimeSync: t("header.getMetricRealTimeSync"),

    GetMetricRealTimeTrainigMaterial: t(
      "header.getMetricRealTimeTrainigMaterial"
    ),

    GetMetricRealTimeVisualPcStatus: t("header.getMetricRealTimeVisual"),

    GetMetricRealTimeVisualPrintStatus: t("header.getMetricRealTimeVisual"),
  };

  const metricDateMap: Record<MetricType, number> = {
    GetMetricRealTimeAbsence: 1,

    GetMetricRealTimeAbsenceScheduling: 1,

    GetMetricRealTimeCheckIn: 1,

    GetMetricRealTimeEsfCoverage: 2,

    GetMetricRealTimeSosPc: 2,

    GetMetricRealTimeSosPrint: 2,

    GetMetricRealTimeSync: 1,

    GetMetricRealTimeTrainigMaterial: 1,

    GetMetricRealTimeVisualPcStatus: 2,

    GetMetricRealTimeVisualPrintStatus: 2,
  };

  useEffect(() => {
    setEmptyMessage(
      metricMessageMap[values.Metrics[0]] ?? t("header.noItemsFound")
    );

    setLoadingMessage(loadingMessageMap[values.Metrics[0]] ?? "");

    generateDate(metricDateMap[values.Metrics[0]]);
  }, [values.Supervisor, values.Metrics, languageID]);

  useEffect(() => {
    if (
      values.Coordinator.length > 0 &&
      values.Supervisor.length > 0 &&
      values.Metrics.length > 0
    ) {
      onClickObtainMetrics(false);
    } else if (
      userRoleIdLogged === 30 &&
      values.Supervisor.length > 0 &&
      values.Metrics.length > 0
    ) {
      onClickObtainMetrics(false);
    }
  }, [values.Coordinator, values.Supervisor, values.Metrics]);

  const updateOptions = (
    key: keyof IOptionsMetricsRealTime,
    value: SelectOption[]
  ) => {
    setOptionSelect((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const memoizedHandleValidate = useMemo(
    () => handleValidate,
    [handleValidate]
  );

  useEffect(() => {
    const hasAtLeastOneFalse = Object.values(memoizedHandleValidate).some(
      (value) => value === true
    );

    if (hasAtLeastOneFalse) {
      return setIsButtonValid(true);
    } else {
      return setIsButtonValid(false);
    }
  }, [memoizedHandleValidate]);

  useEffect(() => {
    getCoordinatorBySubRegion();
    if (userRoleIdLogged === 30) {
      getSupervisorByCoordinator(0);
      setHandleValidate((prevState) => ({
        ...prevState,
        Coordinator: false,
        Supervisor: false,
      }));
    }
  }, []);

  useEffect(() => {
    if (languageID?.value) {
      getMetricsList(languageID.value);
    }
  }, [languageID]);

  const applyFilters = (activeFilters: FilterableFields) => {
    let filtered = tableDataRef.current;

    Object.entries(activeFilters).forEach(([field, value]) => {
      if (!value) return;

      const query = value.toLowerCase().trim();

      filtered = filtered.filter((row) => {
        const cellValue = row[field as keyof FilterableFields];
        return (
          typeof cellValue === "string" &&
          cellValue.toLowerCase().includes(query)
        );
      });
    });

    setCurrentPage(1);
    setTableData(filtered);
  };

  const handleColumnSearch = (value: string, field: keyof FilterableFields) => {
    setFilters((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [field]: value,
      };

      applyFilters(newFilters);
      return newFilters;
    });
  };

  const columnConfigMap: Record<string, TableColumns[]> = {
    GetMetricRealTimeAbsence: [
      {
        id: "supervisor",
        label: t("header.supervisor"),
        filter: (
          <TextBox
            id="searchSupervisor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "supervisor")
            }
          />
        ),
      },
      {
        id: "promotor",
        label: t("header.promoter"),
        filter: (
          <TextBox
            id="searchPromotor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "promotor")}
          />
        ),
      },
      { id: "dias_De_CheckIn", label: t("header.CheckInDays") },
      { id: "dias_Laborales", label: t("header.WorkingDays") },
      { id: "dias_De_Ausencias", label: t("header.AbsenceDays") },
      {
        id: "status",
        label: t("header.status"),
        filter: (
          <TextBox
            id="searchStatus"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "status")}
          />
        ),
      },
    ],
    GetMetricRealTimeAbsenceScheduling: [
      {
        id: "supervisor",
        label: t("header.supervisor"),
        filter: (
          <TextBox
            id="searchSupervisor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "supervisor")
            }
          />
        ),
      },
      {
        id: "promotor",
        label: t("header.promoter"),
        filter: (
          <TextBox
            id="searchPromotor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "promotor")}
          />
        ),
      },
      { id: "ausencias", label: t("header.absences") },
      { id: "dias_Laborales", label: t("header.workedDays") },
      {
        id: "status",
        label: t("header.status"),
        filter: (
          <TextBox
            id="searchStatus"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "status")}
          />
        ),
      },
      {
        id: "descripcion",
        label: t("header.description"),
        filter: (
          <TextBox
            id="searchDescription"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "descripcion")
            }
          />
        ),
      },
    ],
    GetMetricRealTimeCheckIn: [
      { id: "photo", label: t("header.photo") },
      {
        id: "supervisor",
        label: t("header.supervisor"),
        filter: (
          <TextBox
            id="searchSupervisor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "supervisor")
            }
          />
        ),
      },
      {
        id: "promotor",
        label: t("header.promoter"),
        filter: (
          <TextBox
            id="searchPromotor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "promotor")}
          />
        ),
      },
      {
        id: "posCode",
        label: t("header.posOfCode"),
        filter: (
          <TextBox
            id="searchPosCode"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "posCode")}
          />
        ),
      },
      {
        id: "punto_De_Venta",
        label: t("header.pointOfSale"),
        filter: (
          <TextBox
            id="searchPointOfSale"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "punto_De_Venta")
            }
          />
        ),
      },
      {
        id: "checkIn_Programado",
        label: t("header.scheduledCheckIn"),
        filter: (
          <TextBox
            id="searchScheduledCheckIn"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "checkIn_Programado")
            }
          />
        ),
      },
      {
        id: "fecha_De_Registro",
        label: t("header.registrationDate"),
        filter: (
          <TextBox
            id="searchRegistrationDate"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "fecha_De_Registro")
            }
          />
        ),
      },
      {
        id: "status",
        label: t("header.status"),
        filter: (
          <TextBox
            id="searchStatus"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "status")}
          />
        ),
      },
    ],
    GetMetricRealTimeEsfCoverage: [
      {
        id: "supervisor",
        label: t("header.supervisor"),
        filter: (
          <TextBox
            id="searchSupervisor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "supervisor")
            }
          />
        ),
      },
      {
        id: "posCode",
        label: t("header.posOfCode"),
        filter: (
          <TextBox
            id="searchPosCode"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "posCode")}
          />
        ),
      },
      {
        id: "pos",
        label: t("header.pos"),
        filter: (
          <TextBox
            id="searchPointOfSale"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "punto_De_Venta")
            }
          />
        ),
      },
      { id: "subtotal", label: t("header.executedRecords") },
      { id: "total", label: t("header.plannedRecords") },
      {
        id: "metrica",
        label: t("header.metrics"),
        filter: (
          <TextBox
            id="searchMetrics"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "metrica")}
          />
        ),
      },
    ],
    GetMetricRealTimeSosPc: [
      {
        id: "supervisor",
        label: t("header.supervisor"),
        filter: (
          <TextBox
            id="searchSupervisor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "supervisor")
            }
          />
        ),
      },
      {
        id: "posCode",
        label: t("header.posOfCode"),
        filter: (
          <TextBox
            id="searchPosCode"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "posCode")}
          />
        ),
      },
      {
        id: "pos",
        label: t("header.pos"),
        filter: (
          <TextBox
            id="searchPointOfSale"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "punto_De_Venta")
            }
          />
        ),
      },
      { id: "subtotal", label: t("header.executedRecords") },
      { id: "total", label: t("header.plannedRecords") },
      {
        id: "metrica",
        label: t("header.metrics"),
        filter: (
          <TextBox
            id="searchMetrics"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "metrica")}
          />
        ),
      },
    ],
    GetMetricRealTimeSosPrint: [
      {
        id: "supervisor",
        label: t("header.supervisor"),
        filter: (
          <TextBox
            id="searchSupervisor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "supervisor")
            }
          />
        ),
      },
      {
        id: "posCode",
        label: t("header.posOfCode"),
        filter: (
          <TextBox
            id="searchPosCode"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "posCode")}
          />
        ),
      },
      {
        id: "pos",
        label: t("header.pos"),
        filter: (
          <TextBox
            id="searchPointOfSale"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "punto_De_Venta")
            }
          />
        ),
      },
      { id: "subtotal", label: t("header.executedRecords") },
      { id: "total", label: t("header.plannedRecords") },
      {
        id: "metrica",
        label: t("header.metrics"),
        filter: (
          <TextBox
            id="searchMetrics"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "metrica")}
          />
        ),
      },
    ],
    GetMetricRealTimeSync: [
      {
        id: "supervisor",
        label: t("header.supervisor"),
        filter: (
          <TextBox
            id="searchSupervisor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "supervisor")
            }
          />
        ),
      },
      {
        id: "promotor",
        label: t("header.promoter"),
        filter: (
          <TextBox
            id="searchPromotor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "promotor")}
          />
        ),
      },
      { id: "dias_Sincronizados", label: t("header.synchronizedDays") },
      { id: "dias_Laborales", label: t("header.workedDays") },
      { id: "porcentaje", label: t("header.percentage") },
      {
        id: "status",
        label: t("header.status"),
        filter: (
          <TextBox
            id="searchStatus"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "status")}
          />
        ),
      },
    ],
    GetMetricRealTimeTrainigMaterial: [
      {
        id: "supervisor",
        label: t("header.supervisor"),
        filter: (
          <TextBox
            id="searchSupervisor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "supervisor")
            }
          />
        ),
      },
      {
        id: "promotor",
        label: t("header.promoter"),
        filter: (
          <TextBox
            id="searchPromotor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "promotor")}
          />
        ),
      },
      {
        id: "cantidad_De_Material_De_Entrenamiento",
        label: t("header.reviewedTrainingMaterial"),
      },
      { id: "dias_Laborales", label: t("header.workedDays") },
      { id: "ausencias", label: t("header.absences") },
      {
        id: "status",
        label: t("header.status"),
        filter: (
          <TextBox
            id="searchStatus"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "status")}
          />
        ),
      },
    ],
    GetMetricRealTimeVisualPcStatus: [
      {
        id: "supervisor",
        label: t("header.supervisor"),
        filter: (
          <TextBox
            id="searchSupervisor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "supervisor")
            }
          />
        ),
      },
      {
        id: "posCode",
        label: t("header.posOfCode"),
        filter: (
          <TextBox
            id="searchPosCode"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "posCode")}
          />
        ),
      },
      {
        id: "pos",
        label: t("header.pos"),
        filter: (
          <TextBox
            id="searchPointOfSale"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "punto_De_Venta")
            }
          />
        ),
      },
      { id: "cantidad_de_HP", label: t("header.hpQuantity") },
      {
        id: "cantidad_de_Competencia",
        label: t("header.quantityOfCompetition"),
      },
      {
        id: "status",
        label: t("header.status"),
        filter: (
          <TextBox
            id="searchStatus"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "status")}
          />
        ),
      },
    ],
    GetMetricRealTimeVisualPrintStatus: [
      {
        id: "supervisor",
        label: t("header.supervisor"),
        filter: (
          <TextBox
            id="searchSupervisor"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "supervisor")
            }
          />
        ),
      },
      {
        id: "posCode",
        label: t("header.posOfCode"),
        filter: (
          <TextBox
            id="searchPosCode"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "posCode")}
          />
        ),
      },
      {
        id: "pos",
        label: t("header.pos"),
        filter: (
          <TextBox
            id="searchPointOfSale"
            label={t("header.search")}
            type="search"
            onChange={(value: string) =>
              handleColumnSearch(value, "punto_De_Venta")
            }
          />
        ),
      },
      { id: "cantidad_de_HP", label: t("header.hpQuantity") },
      {
        id: "cantidad_de_Competencia",
        label: t("header.quantityOfCompetition"),
      },
      {
        id: "status",
        label: t("header.status"),
        filter: (
          <TextBox
            id="searchStatus"
            label={t("header.search")}
            type="search"
            onChange={(value: string) => handleColumnSearch(value, "status")}
          />
        ),
      },
    ],
  };

  const getTableColumns = (name: string): TableColumns[] => {
    return columnConfigMap[name];
  };

  const pagedData = tableData.slice(
    (currentPage - 1) * pageSize,
    (currentPage - 1) * pageSize + pageSize
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (
    event: React.MouseEvent<HTMLElement>,
    option: OptionInterface<number>
  ) => {
    setPageSize(option.value);
  };

  const paginationProps: TablePagination = {
    currentPage,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSize,
    totalItems: tableData.length,
  };

  const generateDate = (value: number) => {
    const today = moment();

    if (value === 1) {
      const monday = today.clone().startOf("isoWeek");

      setValues((prevState) => ({
        ...prevState,
        StartDate: monday.toDate(),
        EndDate: today.toDate(),
      }));
    }

    if (value === 2) {
      const firstDayOfMonth = today.clone().startOf("month");

      setValues((prevState) => ({
        ...prevState,
        StartDate: firstDayOfMonth.toDate(),
        EndDate: today.toDate(),
      }));
    }
  };

  const getCoordinatorBySubRegion = () => {
    dataListService
      .getCoordinatorBySubRegion()
      .then((result: ICoordinatorBySubRegion[]) => {
        const updatedOptions = result.map((item) => ({
          value: item.coordinatorId,
          label: item.fullName,
        }));
        updateOptions("Coordinator", updatedOptions);
        updateOptions("FilteredCoordinator", updatedOptions);
      });
  };

  const getSupervisorByCoordinator = (CoordinatorId: number) => {
    dataListService
      .getSupervisorByCoordinator(CoordinatorId)
      .then((result: ISupervisorByCoordinator[]) => {
        const updatedOptions = result.map((item) => ({
          value: item.supervisorID,
          label: item.fullName,
        }));
        if (updatedOptions[0].value === 0) {
          updatedOptions[0].label = t("header.noSupervisorsAssigned");
        }
        updateOptions("Supervisor", updatedOptions);
        updateOptions("FilteredSupervisor", updatedOptions);
      });
  };

  const getMetricsList = (language: any) => {
    metricsRealTimeService
      .GetMetricsList(language)
      .then((result: IMetricsList[]) => {
        const updatedOptions = result.map((item) => ({
          value: item.controllers,
          label: item.metrics,
        }));
        updateOptions("Metrics", updatedOptions);
        updateOptions("FilteredMetrics", updatedOptions);
      });
  };

  const onChangeCoordinator = (selectedOption: SelectOption) => {
    setValues((prevState) => ({
      ...prevState,
      Coordinator: [Number(selectedOption.value)],
      Supervisor: [],
    }));
    setForm((prevState) => ({
      ...prevState,
      SupervisorID: 0,
    }));
    setHandleValidate((prevState) => ({
      ...prevState,
      Coordinator: false,
    }));
    getSupervisorByCoordinator(Number(selectedOption.value));
  };

  const onChangeSupervisor = (selectedOption: SelectOption) => {
    setValues((prevState) => ({
      ...prevState,
      Supervisor: [Number(selectedOption.value)],
    }));
    setForm((prevState) => ({
      ...prevState,
      SupervisorID: Number(selectedOption.value),
    }));
    setHandleValidate((prevState) => ({
      ...prevState,
      Supervisor: false,
    }));

    if (selectedOption.value === 0) {
      setHandleValidate((prevState) => ({
        ...prevState,
        Supervisor: true,
      }));
    }
  };

  const onChangeMetrics = (selectedOption: MetricOption) => {
    setValues((prevState) => ({
      ...prevState,
      Metrics: [selectedOption.value],
      Columns: [],
    }));
    setForm((prevState) => ({
      ...prevState,
      Metrics: selectedOption.value,
    }));
    setHandleValidate((prevState) => ({
      ...prevState,
      Metrics: false,
    }));
  };

  const onClearSelect = (name: string) => {
    setHandleValidate((prevState) => ({
      ...prevState,
      [name]: true,
    }));
    setValues((prevState) => ({
      ...prevState,
      [name]: [],
    }));

    if (name === "Coordinator") {
      getSupervisorByCoordinator(0);
    }

    if (name === "Supervisor") {
      setForm((prevState) => ({
        ...prevState,
        SupervisorID: 0,
      }));
    } else if (name === "Metrics") {
      setForm((prevState) => ({
        ...prevState,
        Metrics: null,
      }));
    }
  };

  const disableSupervisor = (values: IValuesMetricsRealTime) => {
    if (values.Coordinator[0] !== undefined || userRoleIdLogged === 30) {
      return false;
    } else {
      return true;
    }
  };

  function onClickObtainMetrics(downloadMetric: boolean): void {
    setCurrentPage(1);

    if (downloadMetric) {
      if (!form.Metrics) {
        return;
      }
      let nameExcel = form.Metrics.replace("Get", "") + ".xlsx";

      if (nameExcel === "MetricRealTimeSosPrint.xlsx") {
        nameExcel = nameExcel.replace("Print", "");
      } else if (nameExcel === "MetricRealTimeVisualPrintStatus.xlsx") {
        nameExcel = nameExcel.replace("PrintStatus", "");
      }

      metricsRealTimeService
        .downloadMetric(
          languageID.value,
          form.SupervisorID,
          form.Metrics.replace("Get", "Download")
        )
        .then((result) => {
          if (result instanceof Blob) {
            const url = window.URL.createObjectURL(result);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", nameExcel);
            document.body.appendChild(link);
            link.click();
          }
        });
      return;
    }

    setTableData([]);
    tableDataRef.current = [];

    if (!form.Metrics) return;

    const column = getTableColumns(form.Metrics);
    setValues((prev) => ({ ...prev, Columns: column }));
    setLoading(true);

    metricsRealTimeService
      .GetMetricsRealTime(form.SupervisorID, form.Metrics)
      .then((result) => {
        // Helper type guard
        function hasStatus<T extends object>(
          item: T
        ): item is T & { status: string } {
          return "status" in item;
        }

        if (
          result.length > 0 &&
          hasStatus(result[0]) &&
          result[0].status === "noAdvisorsAssigned"
        ) {
          setEmptyMessage(t("header.noAdvisorAssigned"));
          setLoading(false);
          return;
        }

        // Map según tipo de métrica
        let updatedData: (
          | MetricAbsenceCheckIn
          | MetricAbsence
          | MetricsCheckIn
          | MetricSos
          | MetricSync
          | MetricsTrainingMaterial
          | MetricsVisualStatus
        )[] = [];

        switch (form.Metrics) {
          case "GetMetricRealTimeAbsence": {
            const data = result as unknown as MetricAbsenceCheckIn[];
            updatedData = data.map((item) => ({
              ...item,
              dias_De_Ausencias: String(item.dias_De_Ausencias),
              dias_De_CheckIn: String(item.dias_De_CheckIn),
              dias_Laborales: String(item.dias_Laborales),
            }));
            break;
          }

          case "GetMetricRealTimeSync": {
            const data = result as unknown as MetricSync[];
            updatedData = data.map((item) => ({
              ...item,
              dias_Laborales: String(item.dias_Laborales),
              dias_Sincronizados: String(item.dias_Sincronizados),
              porcentaje: String(item.porcentaje) + "%",
            }));
            break;
          }

          case "GetMetricRealTimeAbsenceScheduling": {
            const data = result as unknown as MetricAbsence[];
            updatedData = data.map((item) => ({
              ...item,
              dias_Laborales: String(item.dias_Laborales),
              ausencias: String(item.ausencias),
            }));
            break;
          }

          case "GetMetricRealTimeTrainigMaterial": {
            const data = result as unknown as MetricsTrainingMaterial[];
            updatedData = data.map((item) => ({
              ...item,
              ausencias: String(item.ausencias),
              cantidad_De_Material_De_Entrenamiento: String(
                item.cantidad_De_Material_De_Entrenamiento
              ),
            }));
            break;
          }

          case "GetMetricRealTimeEsfCoverage":
          case "GetMetricRealTimeSosPc":
          case "GetMetricRealTimeSosPrint": {
            const data = result as unknown as MetricSos[];
            updatedData = data.map((item) => ({
              ...item,
              subtotal: String(item.subtotal),
              total: String(item.total),
              metrica: String(item.metrica) + "%",
            }));
            break;
          }

          case "GetMetricRealTimeVisualPcStatus":
          case "GetMetricRealTimeVisualPrintStatus": {
            const data = result as unknown as MetricsVisualStatus[];
            updatedData = data.map((item) => ({
              ...item,
              cantidad_de_HP: String(item.cantidad_de_HP),
              cantidad_de_Competencia: String(item.cantidad_de_Competencia),
            }));
            break;
          }

          case "GetMetricRealTimeCheckIn": {
            const data = result as unknown as MetricsCheckIn[];
            updatedData = data.map((item) => ({
              ...item,
              checkIn_Programado:
                item.status === "Sin Agendas" || item.status === "Sem agendas"
                  ? ""
                  : item.checkIn_Programado,
              fecha_De_Registro:
                item.status === "Sin Agendas" || item.status === "Sem agendas"
                  ? ""
                  : item.fecha_De_Registro,
            }));
            break;
          }
        }

        // Convertir a TableRow
        const tableRows: TableRow[] = updatedData.map((item) => {
          const row: TableRow = {
            rowConfig: { disabled: false, noWrap: false, selected: false },
          };

          column.forEach((col) => {
            if (col.id === "photo") {
              const photoItem = item as any;
              if ("photo" in photoItem && typeof photoItem.photo === "string") {
                row[col.id] =
                  photoItem.photo === "" ? (
                    <div className="DivIconCustomNoItemsRealTime">
                      <CustomImage
                        size={55}
                        quietArea={10}
                        src={IconUserNotFound}
                      />
                    </div>
                  ) : (
                    <CustomImage
                      size={100}
                      quietArea={0}
                      src={photoItem.photo}
                    />
                  );
              } else {
                row[col.id] = "";
              }
            } else {
              const value =
                col.id in item ? (item as Record<string, any>)[col.id] : "";
              row[col.id] =
                typeof value === "string" ? value : String(value ?? "");
            }
          });

          return row;
        });

        setTableData(tableRows);
        tableDataRef.current = tableRows;
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }

  return (
    <>
      <div style={{ padding: "1%" }}>
        <h4>{t("header.realTimeMetrics")}</h4>
      </div>

      <div className="realTime-container">
        <Select
          className="realTime-select"
          clearIcon={true}
          options={optionSelect.Coordinator}
          id="selectCoordinator"
          label={t("header.coordinator")}
          placement="bottom"
          helperTextVisibility="auto"
          onChange={(selectedOption: SelectOption) =>
            onChangeCoordinator(selectedOption)
          }
          value={values.Coordinator}
          required={handleValidate.Coordinator}
          onSearch={(query: string) =>
            handleSearch(
              query,
              "Coordinator",
              optionSelect.FilteredCoordinator,
              setOptionSelect
            )
          }
          searchDelay={500}
          showSearch={true}
          onClear={() => onClearSelect("Coordinator")}
        />
        <Select
          className="realTime-select"
          clearIcon={true}
          options={optionSelect.Supervisor}
          id="selectSupervisor"
          label={t("header.supervisor")}
          placement="bottom"
          helperTextVisibility="auto"
          onChange={(selectedOption: SelectOption) =>
            onChangeSupervisor(selectedOption)
          }
          value={values.Supervisor}
          disabled={disableSupervisor(values)}
          required={handleValidate.Supervisor}
          onSearch={(query: string) =>
            handleSearch(
              query,
              "Supervisor",
              optionSelect.FilteredSupervisor,
              setOptionSelect
            )
          }
          searchDelay={500}
          showSearch={true}
          onClear={() => onClearSelect("Supervisor")}
        />
        <Select
          className="realTime-select"
          clearIcon={true}
          options={optionSelect.Metrics}
          id="selectMetrics"
          label={t("header.metrics")}
          placement="bottom"
          helperTextVisibility="auto"
          onChange={(selectedOption: MetricOption) =>
            onChangeMetrics(selectedOption)
          }
          value={values.Metrics}
          required={handleValidate.Metrics}
          onSearch={(query: string) =>
            handleSearch(
              query,
              "Metrics",
              optionSelect.FilteredMetrics,
              setOptionSelect
            )
          }
          searchDelay={500}
          showSearch={true}
          onClear={() => onClearSelect("Metrics")}
        />
        <DatePicker
          className="realTime-datePicker"
          endDate={values.EndDate ? values.EndDate.toISOString() : undefined}
          format="ll"
          clearIcon={false}
          range
          startDate={
            values.StartDate ? values.StartDate.toISOString() : undefined
          }
          startDateLabel="Start Date"
          endDateLabel="End Date"
          disabled={true}
        />

        <div className="tooltipRealTime">
          <span className="tooltiptextRealTime">
            {t("header.downloadMetrics")}
          </span>
          <Button
            onClick={() => onClickObtainMetrics(true)}
            style={{ margin: "2px" }}
            appearance="secondary"
            leadingIcon={<IconDocument />}
            disabled={isButtonValid}
          ></Button>
        </div>
      </div>

      {values.Columns.length > 0 && (
        <Card
          appearance="outlined"
          className="divTableRealTime"
          content={
            <Table
              className="tableRealTime"
              columnResize
              loading={loading}
              columns={values.Columns}
              data={pagedData}
              loadingDataLength={5}
              pagination={paginationProps}
              showFilters={true}
              behavior={{
                type: "singlePage",
              }}
              customNoItems={
                <div className="customNoItems">
                  <div className="DivIconCustomNoItems">
                    <IconWarningAlt className="IconCustomNoItems" />
                  </div>
                  {emptyMessage}
                </div>
              }
            />
          }
        />
      )}

      {loading && (
        <div>
          <LoaderModal>
            <div> {loadingMessage}</div>
          </LoaderModal>
        </div>
      )}
    </>
  );
}
